import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Stage, Layer, Group, Rect, Circle, Path } from 'react-konva';
import { affect_color_filter, colorToArray, getPromotionMainColor, getPromotionTextColor, shuffleArray } from '../../lib/ep.js';
import { Confetti } from './Particles.js';
import EditableArea from '../canvas/EditableArea.js';
import { adaptImageSizes,  getAbsoluteCoords, getAbsoluteCoordsAsymmetrical } from '../canvas/utils.js';
import Konva from 'konva';

const canvas_w = 360;		// px
const canvas_h = 580;		// px
const punch_item_size = 60;	// px
const canvas_padding = 10;	// px
const punch_padding = 1.1;	// in % (1 - x to get the % of padding)
const guides_color = "#FF0000";
const colors = [
	'#2951CC',
	'#FF9800',
	'#75C400',
	'#FFE045',
	'#D8253B',
	'#85428E',
];

const StamperHolder = (props) => {
	return (
		<Group>
			<Path
				fill="#999999"
				data="M48,0c-18,9.85-3,16.81-3,28c0,8.28-6.72,15-15,15s-15-6.72-15-15c0-11.19,15-18.15-3-28H48z"
			/>

			<Path
				fill="#808080"
				opacity={0.5}
				data="M30,25c-4.93,0-9.43-1.79-12.92-4.74c-0.34,0.72-0.67,1.46-0.96,2.22C19.44,26.37,24.08,29,30,29c5.91,0,10.55-2.63,13.87-6.53c-0.29-0.75-0.61-1.49-0.96-2.21C39.43,23.21,34.93,25,30,25z"
			/>

			<Path
				stroke="#666666"
				strokeWidth={0.8}
				data="M48,0c-18,9.85-3,16.81-3,28c0,8.28-6.72,15-15,15s-15-6.72-15-15c0-11.19,15-18.15-3-28H48z"
			/>

			<Circle
				fill="#B3B3B3"
				stroke="#666666"
				strokeWidth={1.2}
				x={punch_item_size / 2}
				y={5}
				radius={20}
			/>

			<Path
				fill="#666666"
				stroke="#666666"
				strokeWidth={0.6}
				data="M30,23.5c11.05,0,19.94-8.95,19.94-20c0,0,0.06,1.12,0.06,1.5c0,11.05-8.95,20-20,20S10,16.05,10,5c0-0.38,0.06-1.5,0.06-1.5C10.06,14.55,18.95,23.5,30,23.5z"
			/>
		</Group>
	)
};

const SquaredStamper = ({ refs, ...props }) => {
	return (
		<>
			<Group>
				<Rect
					fill="#808080"
					stroke="#666666"
					strokeWidth={1}
					x={0}
					y={0}
					width={punch_item_size}
					height={punch_item_size}
				/>

				<Rect
					fill="#666666"
					stroke="#666666"
					strokeWidth={0.5}
					x={0}
					y={58.56}
					width={punch_item_size}
					height={1.44}
				/>
			</Group>

			<StamperHolder
				{...props}
			/>
		</>
	)
};

const RoundedStamper = ({ refs, ...props }) => {
	return (
		<>
			<Group>
				<Circle
					fill="#808080"
					stroke="#666666"
					strokeWidth={1}
					x={punch_item_size / 2}
					y={punch_item_size / 2}
					radius={punch_item_size / 2}
				/>

				<Path
					fill="#666666"
					stroke="#666666"
					strokeWidth={0.5}
					data="M30,58c16.57,0,29.93-13.43,29.93-30c0,0,0.07,1.49,0.07,2c0,16.57-13.43,30-30,30S0,46.57,0,30c0-0.51,0.07-2,0.07-2C0.07,44.57,13.43,58,30,58z"
				/>
			</Group>

			<StamperHolder
				{...props}
			/>
		</>
	)
};

const PunchCardCanvas = forwardRef((props, ref) => {
	const stage = props.stage;
	const is_builder = props?.is_builder ?? false;
	const is_editing = (is_builder) ? (props?.is_editing ?? false) : false;

	const is_playable = props?.is_playable ?? true;
	const only_display = props?.only_display ?? false;
	const show_grid = (!is_playable && is_editing) ? true : false;

	const num_punches = parseInt(stage?.punch_num_punches ?? 5);
	const punch_distribution = stage?.punch_distribution ?? 'honeycomb';
	const punch_shape = stage?.punch_shape ?? 'circle';
	const display_punch_numbers = stage?.punch_display_punch_numbers ?? true;
	const punch_item_border_width = 2;
	const current_stamps = props?.current_stamps ?? 0;
	const coming_stamps = parseInt(props?.coming_stamps ?? 1);
	const force_display_stamps = props?.force_display_stamps ?? false;
	const text_color = stage?.punch_text_color ?? '#000000';
 
	const card_rounds = Math.ceil((current_stamps + coming_stamps) / num_punches);

	const pending_stamps_in_card = num_punches - current_stamps;
	const stamps_in_next_card = (current_stamps + coming_stamps) % num_punches;

	const color_empty = stage?.punch_off_color ?? `rgba(${colorToArray(getPromotionTextColor(props.promotion))}, 0.2)`;
	const color_spent = stage?.punch_on_color ?? getPromotionMainColor(props.promotion);
	const randomly_position = stage?.punch_randomly_position ?? true;
 
	const refs = useRef([]);
	const max_grid_span = useRef({});
	const all_container = useRef(false);
	const already_loaded = useRef(false);
	const num_card = useRef(0);
	const confetti = useRef();
	const stamp_buffer = useRef([]);
	const stamp_index = useRef(0);
	const prestop = useRef(false);
	const apply_shitty_ios_hack = useRef(['iphone', 'ipad'].some((cl) => document.querySelector('html').classList.contains(cl)));

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };
	const stamper_speed_ini = 0.15;	// in sec
	const stamper_speed_end = 0.2;	// in sec

	const relative_group_area = {...(stage?.punch_group_area ?? { w:1, h:1, x:0, y:0 })};

	const applyShittyIOSHack = () => {
		if(!apply_shitty_ios_hack.current) return;

		setTimeout(() => {
			const canvas_dom = document.querySelector('.konvajs-content canvas');
			canvas_dom.style.width = 'unset';
			canvas_dom.style.height = 'unset';
			canvas_dom.style.maxWidth = '100%';
			canvas_dom.style.maxHeight = '100%';
		}, 1500);
	};

	useEffect(() => {
		applyShittyIOSHack();
	}, []);

	useEffect(() => {
		if(refs.current['layer'] == undefined) return;

		gameInit();

		refs.current['layer'].draw();
	}, [ props.stage.punch_text_color, props.stage.punch_off_color ]);

	
	useImperativeHandle(ref, () => ({
		addStamp(stamp) {
			stamp_buffer.current.push(stamp);
		}
	}), []);

	const images_adapter = (image) => (adaptImageSizes(image,punch_item_size,300,500));

	const indexed_images = useMemo(()=>{
		const adapted = {};
		for(var img in props.indexed_images) {
			adapted[img] = images_adapter(props.indexed_images[img]);
		}
		return adapted;
	}, [ props.indexed_images ]);

	const getCardSize = () => {
		return {
			width:all_container.current.width + 2 * canvas_padding,
			height:all_container.current.height + 2 * canvas_padding,
		};
	};

	const adaptCardContainer = () => {
		refs.current['punches_area_bg'].setAttrs({
			...getCardSize(),
			opacity:1,
		});
		
		refs.current['punches_area_bg'].cache(default_cache_props);
	};

	const gameInit = () => {
		generateCard(1);

		refs.current['stamper'].cache(default_cache_props);
	};

	const assignNewStamps = (items, start) => {
		const _new_stamps = [];
		if(items == undefined || items < 1) return _new_stamps;

		[...Array(items).keys()].map((i) => {
			_new_stamps.push(start + i);
		});

		return _new_stamps;
	};

	const getNewStamps = () => {
		const rounds = [assignNewStamps(((card_rounds == 1) ? coming_stamps : pending_stamps_in_card), current_stamps)];

		if(card_rounds == 1) return rounds;

		for(let i = 2; i <= card_rounds; i++) {
			if(i == card_rounds) {
				rounds.push(assignNewStamps(stamps_in_next_card, 0));
				continue;
			}

			rounds.push(assignNewStamps(num_punches, 0));
		}

		return rounds;
	};

	const new_stamps = getNewStamps();

	const group_pos = getAbsoluteCoords(stage.punch_group_area, canvas_w, canvas_h);
	group_pos.width = group_pos.w;
	group_pos.height = group_pos.h;

	const generateGridHoneycomb = () => {
		const punch_bases = {
			1:[1],
			2:[2],
			3:[1, 2],
			4:[1, 2, 1],
			5:[2, 3],
			6:[2, 1, 2, 1],
			7:[2, 3, 2],
			8:[1, 2, 3, 2],
			9:[1, 2, 3, 2, 1],
			10:[4, 3, 2, 1],
			11:[4, 3, 4],
			12:[5, 4, 3],
			13:[4, 5, 4],
			14:[4, 3, 4, 3],
			15:[5, 4, 3, 2, 1],
			16:[5, 4, 3, 4],
			17:[3, 4, 3, 4, 3],
			18:[4, 5, 4, 5],
			19:[3, 4, 5, 4, 3],
			20:[4, 3, 4, 5, 4],
		};

		return punch_bases[num_punches];
	};

	const generateGridUniform = () => {
		const punch_bases = {
			1:[1],
			2:[2],
			3:[3],
			4:[4],
			5:[3, 2],
			6:[3, 3],
			7:[4, 3],
			8:[4, 4],
			9:[3, 3, 3],
			10:[5, 5],
			11:[4, 4, 3],
			12:[4, 4, 4],
			13:[5, 5, 3],
			14:[5, 5, 4],
			15:[5, 5, 5],
			16:[4, 4, 4, 4],
			17:[5, 5, 5, 2],
			18:[5, 5, 5, 3],
			19:[5, 5, 5, 4],
			20:[5, 5, 5, 5],
		};

		return punch_bases[num_punches];
	};

	const getMaxItemsInRow = (distribution) => {
		let max = 0;
		distribution.map((d) => {
			max = Math.max(max, d);
		});

		return max;
	};

	const getColDisplacement = () => {
		if(punch_shape == 'rect') return 0;
		if(punch_shape == 'circle') return 0;
		return 0;
	};

	const getRowDisplacement = () => {
		if(punch_shape == 'rect') return 0;
		if(punch_shape == 'circle') {
			if(punch_distribution == 'honeycomb') return -0.15 * punch_item_size;
			return 0;
		}
		return 0;
	};

	const getShapeDisplacement = (is_stroke = false) => {
		if(punch_shape == 'rect') {
			if(!is_stroke) return 0;
			return punch_item_border_width / 2;
		}
		if(punch_shape == 'circle') return punch_item_size * 0.5;
		return 0;
	};

	const generateGrid = () => {
		let actual_punch_distribution;
		
		switch(punch_distribution) {
			case 'honeycomb':
			default:
				actual_punch_distribution = generateGridHoneycomb();
				break;

			case 'uniform':
				actual_punch_distribution = generateGridUniform();
				break;
		}

		const grid = [];

		const max_row = getMaxItemsInRow(actual_punch_distribution);

		max_grid_span.current = { x_min:0, y_min:0, x_max:0, y_max:0 };

		actual_punch_distribution.map((row, r) => {
			const x_displacement = (punch_distribution == 'honeycomb') ? (max_row - row) / 2 : 0;
			const y_displacement = 0;

			[...Array(row).keys()].map((col, c) => {
				const punch_x = (c + x_displacement) * punch_padding * punch_item_size + c * getColDisplacement();
				const punch_y = (r + y_displacement) * punch_padding * punch_item_size + r * getRowDisplacement();

				grid.push({
					x:punch_x,
					y:punch_y,
					w:punch_item_size,
					h:punch_item_size,
				});

				max_grid_span.current = {
					x_min:Math.min(max_grid_span.current.x_min, punch_x),
					y_min:Math.min(max_grid_span.current.y_min, punch_y),
					x_max:Math.max(max_grid_span.current.x_max, punch_x),
					y_max:Math.max(max_grid_span.current.y_max, punch_y),
				};
			});
		});

		return grid;
	};

	const getAllPunchesSize = () => {
		return {
			x:max_grid_span.current.x_min,
			y:max_grid_span.current.y_min,
			width:max_grid_span.current.x_max - max_grid_span.current.x_min + punch_item_size,
			height:max_grid_span.current.y_max - max_grid_span.current.y_min + punch_item_size,
		};
	};

	const grid = useRef(generateGrid());

	const areas_group_pos = getAbsoluteCoordsAsymmetrical(relative_group_area, canvas_w, canvas_h);

	const calcAbsoluteStampPosition = (x, y) => {
		return {
			x:x + canvas_padding + (areas_group_pos?.x ?? 0) + punch_item_size / 2,
			y:y + canvas_padding + (areas_group_pos?.y ?? 0) + punch_item_size / 2,
		}
	};

	const stamping_positions = [];

	[...Array(coming_stamps).keys()].map((_index) => {
		const index = (_index + current_stamps) % grid.current.length;

		const square = grid.current[index];

		// to center the confetti explosion:
		const abs_pos = calcAbsoluteStampPosition(square.x, square.y);

		const real_index = index + 1;
		let display_this = false;

		// enough stamp positions before finishing the card:
		if(real_index <= grid.current.length) {
			display_this = true;
		}

		// next card pending stamp positions:
		if(current_stamps + coming_stamps > grid.current.length && real_index <= ((current_stamps + coming_stamps) % grid.current.length)) {
			display_this = true;
		}

		if(!display_this) return null;

		stamping_positions.push({
			index,
			abs_x:abs_pos.x,
			abs_y:abs_pos.y,
			x:square.x,
			y:square.y,
		});
	});

	const shootconfetti = (ini_x, ini_y) => {
		const main_color = colors[Math.round(Math.random() * colors.length)];
		const white_color = '#FFFFFF';
		const gray_color = '#CCCCCC';
		const confetti_colors = [
			white_color,
			gray_color,
			affect_color_filter(-0.7, main_color),
			affect_color_filter(-0.5, main_color),
			affect_color_filter(-0.3, main_color),
			main_color,
			affect_color_filter(0.3, main_color),
			affect_color_filter(0.5, main_color),
			affect_color_filter(0.7, main_color),
		];

		const knvGenerate = (x, y) => {
			const particle_width  = Math.floor(Math.random() * 6) + 2;
			const particle_height = Math.floor(Math.random() * 8) + 3;

			return new Konva.Circle({
				radius:particle_width,
				offsetX:particle_width / 2,
				offsetY:particle_height / 2,
				fill:confetti_colors[Math.floor(Math.random() * confetti_colors.length) + 1],
				x,
				y,
			});
		};

		// CONFETTI EXPLOSION:
		confetti.current.shoot({
			source:{ x:ini_x, y:ini_y },
			vector:[0, -8],
			dir_variance:[5, 8],
			particles:150,
			knv:knvGenerate,
		});
	};

	const finish = () => {
		confetti.current.stop();

		if(props.showContinueButton != undefined) {
			setTimeout(() => {
				props.showContinueButton();
			}, 2000);
		}
	};

	const stampThis = (index, stamp_index) => {
		const stamping_coords = stamping_positions[stamp_index];

		const stamp_center_x = stamping_coords?.abs_x ?? (canvas_w / 2);
		const stamp_center_y = stamping_coords?.abs_y ?? (canvas_h / 2);

		if(stamp_index != undefined && stamp_buffer.current[stamp_buffer.current.length - 1] != undefined) {
			const has_more_retries = (stamp_buffer.current[stamp_buffer.current.length - 1].retries > 0) ? true : false;

			if(has_more_retries === false) {
				if(stamp_index == stamp_buffer.current.length - 1) {
					prestop.current = true;
				}
			}
		}

		if(stamp_index != undefined && stamp_buffer.current[stamp_index] == undefined && prestop.current === false) {
			setTimeout(() => {
				stampThis(index, stamp_index);
			}, 200);
			return;
		}

		refs.current['stamper'].setAttrs({
			x:stamping_coords.x + 60,
			y:stamping_coords.y - 120,
			rotation:36,
			opacity:0,
		});

		refs.current['stamper'].to({
			x:stamping_coords.x,
			y:stamping_coords.y,
			rotation:0,
			opacity:1,
			duration:stamper_speed_ini,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				spendStamp(num_card.current, index);

				const has_prize = stamp_buffer.current[stamp_index]?.prize ?? false;

				if(has_prize) {
					shootconfetti(stamp_center_x, stamp_center_y);
				}

				setTimeout(() => {
					refs.current['stamper'].to({
						x:stamping_coords.x + 60,
						y:stamping_coords.y - 120,
						rotation:24,
						opacity:0,
						duration:stamper_speed_end,
						easing:Konva.Easings.StrongEaseIn,
					});

					const is_last_stamp = (index == num_punches - 1) ? true : false;
					if(stamp_index == coming_stamps - 1 || prestop.current === true) {
						finish();
						return;
					}

					if(is_last_stamp && card_rounds > num_card.current) {
						setTimeout(() => {
							goNextCard();
						}, 1000);
					}
				}, 500);
			},
		});
	};

	const animateStamps = (actual_card = 0) => {
		if(is_builder || only_display) return;

		const interval_between_stamps = Math.max(250, 2000 / coming_stamps);

		new_stamps[actual_card].map((stamp, s) => {
			setTimeout(() => {
				stampThis(stamp, stamp_index.current);
				stamp_index.current++;
			}, (s * interval_between_stamps * 1.3));
		});
	};

	const regenerateGroupArea = () => {
		const card_size = getCardSize();

		const relative_group_area_ok = {
			num_punches,
			w:card_size.width / canvas_w,
			h:card_size.height / canvas_h,
			x:((canvas_w - card_size.width) / 2) / canvas_w,
			y:((canvas_h - card_size.height) / 2) / canvas_h,
		};

		return relative_group_area_ok;
	};

	const spendStamp = (card_id, index) => {
		//refs.current[`punch_${card_id}_${index}_off`].opacity(0);	// the OFF image needs to still be visible under the ON one
		refs.current[`punch_${card_id}_${index}_on`].opacity(1);
		refs.current[`punch_${card_id}_${index}_on`].cache(default_cache_props);
	};

	const preSpendStamps = (cb) => {
		grid.current.map((stamp_item, index) => {
			const is_spent = (index < current_stamps) ? true : false;
			if(is_spent) spendStamp(1, index);
			
		});

		if(cb != undefined) cb();
	};

	/*
	Each stamp position needs to contain two items (one for "OFF" status and another for "ON")
	so the changing between them doesn't need any time lapse to load and to enable clean cards
	to be generated after the actual card (i.e. with all the stamps in OFF status).
	The OFF status will be visible and the ON status will be invisible (i.e. opacity = 0).
	*/
	const generateStampPositionByStatus = (index, card_id, status = 'off') => {
		const color = (status == 'on') ? color_spent : color_empty;
		let stamp_img = false;
		if(indexed_images[`image_${status}`] != undefined) {
			stamp_img = indexed_images[`image_${status}`];
		};
		if(indexed_images[`image_${status}_${index + 1}`] != undefined) {
			stamp_img = indexed_images[`image_${status}_${index + 1}`];
		}

		const x_pos = (status == 'on' && randomly_position) ? (6 * Math.random() - 3) : 0;
		const y_pos = (status == 'on' && randomly_position) ? (6 * Math.random() - 3) : 0;

		const item_fill_props = {
			fill:color,
			x:getShapeDisplacement() + x_pos,
			y:getShapeDisplacement() + y_pos,
		};

		if(stamp_img !== false) {
			item_fill_props.fillPatternImage = stamp_img.fillPatternImage;
			item_fill_props.fillPatternRepeat = 'no-repeat';
			item_fill_props.fillPatternScaleX = stamp_img.scale;
			item_fill_props.fillPatternScaleY = stamp_img.scale;
			item_fill_props.fillPriority = 'pattern';

			if(punch_shape == 'circle') {
				item_fill_props.fillPatternOffsetX = (stamp_img !== false) ? stamp_img.image_w / 2 : null;
				item_fill_props.fillPatternOffsetY = (stamp_img !== false) ? stamp_img.image_h / 2 : null;
			}
		}

		const item_border_props = {
			strokeWidth:punch_item_border_width,
			stroke:color,
			x:getShapeDisplacement(true) + x_pos,
			y:getShapeDisplacement(true) + y_pos,
			opacity:(stamp_img === false) ? 1 : 0,
		};

		let i_fill, i_border;
		switch(punch_shape) {
			case 'circle':
				i_fill = new Konva.Circle({
					...item_fill_props,
					radius:punch_item_size / 2,
					rotation:(status == 'on' && randomly_position) ? (60 * Math.random() - 30) : 0,
				});

				i_border = new Konva.Circle({
					...item_border_props,
					radius:punch_item_size / 2 - punch_item_border_width / 2,
				});
				break;

			case 'rect':
				i_fill = new Konva.Rect({
					...item_fill_props,
					width:punch_item_size,
					height:punch_item_size,
				});

				i_border = new Konva.Rect({
					...item_border_props,
					width:punch_item_size - punch_item_border_width,
					height:punch_item_size - punch_item_border_width,
				});
				break;
		}

		const group_props = {
			opacity:((card_id == 1 && force_display_stamps) || status == 'off') ? 1 : 0,
		};

		const g = new Konva.Group(group_props);

		refs.current[`punch_${card_id}_${index}_${status}`] = g;
		
		g.add(i_fill);
		g.add(i_border);

		refs.current[`punch_${card_id}_${index}_${status}`].cache(default_cache_props);

		return g;
	};

	const generateCard = (card_id) => {
		const container = refs.current['cards'];
		if(container == undefined) return;

		const card = new Konva.Group();
		refs.current[`card_${card_id}`] = card;

		const bg = new Konva.Rect({
			fill:`rgba(255, 255, 255, 0)`,
		});

		if(card_id == 1) {
			refs.current['punches_area_bg'] = bg;
		}

		const all_punches = new Konva.Group({
			x:canvas_padding,
			y:canvas_padding,
		});
		refs.current[`all_punches_${card_id}`] = all_punches;

		grid.current.map((stamp_item, index) => {
			const is_spent = (index < current_stamps) ? true : false;

			const g = new Konva.Group({
				x:stamp_item.x,
				y:stamp_item.y,
				is_spent,
			});

			if(card_id == 1) {
				const group_off = generateStampPositionByStatus(index, card_id, 'off');

				const g_off = new Konva.Group({
					x:stamp_item.x + canvas_padding,
					y:stamp_item.y + canvas_padding,
					is_spent,
				});

				g_off.add(group_off);
				refs.current['all_punches_off'].add(g_off);

				if(display_punch_numbers) {
					const t = new Konva.Text({
						text:index + 1,
						fill:text_color,
						verticalAlign:'middle',
						align:'center',
						width:punch_item_size,
						height:punch_item_size,
						fontSize:22,
					});
	
					refs.current[`text_index_${index}`] = t;
					refs.current[`text_index_${index}`].cache(default_cache_props);

					g.add(t);
				}
			}

			const group_on = generateStampPositionByStatus(index, card_id, 'on');

			g.add(group_on);

			all_punches.add(g);
		});

		refs.current['all_punches_off'].cache(default_cache_props);

		card.add(bg);
		card.add(all_punches);

		container.add(card);

		all_container.current = getAllPunchesSize();

		adaptCardContainer();
		refs.current['layer']?.draw();
	};

	const prepareFutureCard = (actual_card) => {
		const next_card = actual_card + 1;
		const future_card = actual_card + 2;

		setTimeout(() => {
			num_card.current = next_card;

			generateCard(future_card);
			refs.current['layer'].draw();

			setTimeout(() => {
				animateStamps(actual_card);
			}, 200);
		}, 0);
	};

	const goNextCard = (is_first_load = false) => {
		const actual_card = (!is_first_load) ? num_card.current : 0;

		if(is_first_load) {
			prepareFutureCard(actual_card);
			return;
		}

		refs.current[`all_punches_${actual_card}`].getChildren().map((stamp, s) => {
			setTimeout(() => {
				stamp.to({
					x:-1 * (stamp.x() + canvas_w * (1.2 + Math.random())),
					y:canvas_h * (1 - Math.random()) + stamp.y(),
					rotation:20 * Math.random() - 10,
					duration:1.2 + Math.random(),
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						stamp.destroy();

						if(s == 0) prepareFutureCard(actual_card);
					},
				});
			}, (s * 20));
		});
	};

	useEffect(() => {
		if(refs.current['cards'] != undefined) {
			refs.current['cards'].removeChildren();
			refs.current['all_punches_off'].removeChildren();
		}

		generateCard(1);
	}, [ stage.punch_num_punches, stage.punch_distribution, stage.punch_shape, stage.punch_display_punch_numbers, props.force_display_stamps ]);

	useEffect(() => {
		preSpendStamps(() => {
			goNextCard(true);
		});
	}, [ grid.current ]);

	useEffect(() => {
		const new_group = regenerateGroupArea();

		if(refs.current['layer'] != undefined) {
			setTimeout(() => {
				refs.current['layer'].draw();
			}, 100);
		}

		if(already_loaded.current) props?.areaChanged('group', new_group);
		already_loaded.current = true;
	}, [ stage.punch_num_punches, stage.punch_distribution, stage.punch_shape ]);

	const moveGeneral = (area) => {
		const abs = getAbsoluteCoordsAsymmetrical(area, canvas_w, canvas_h);
		refs.current['punches_container'].setAttrs(abs);
		refs.current['punches_container'].draw();
	};

	const StamperComponent = (punch_shape == 'circle') ? RoundedStamper : SquaredStamper;

	return (
		<Stage
			ref={(node) => { refs.current['main_stage'] = node; }}
			className="canvas_cont"
			width={canvas_w}
			height={canvas_h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Group
					ref={(node) => { refs.current['punches_container'] = node; }}
					{...areas_group_pos}
				>
					<Group ref={(node) => { refs.current['all_punches_off'] = node; }} />

					<Group ref={(node) => { refs.current['cards'] = node; }} />

					<Group
						x={canvas_padding}
						y={canvas_padding}
					>
						<Group
							ref={(node) => { refs.current[`stamper`] = node; }}
							opacity={0}
						>
							<StamperComponent
								color_empty={color_empty}
								color_spent={color_spent}
							/>
						</Group>
					</Group>
				</Group>

				<Confetti
					ref={confetti}
					canvas_size={{ width:canvas_w, height:canvas_h }}
					params={{
						Gravity:0.3,
					}}
				/>

				{/* Done to prevent KonvaJS to block scrolling on mobile devices (https://konvajs.org/docs/events/Mobile_Scrolling.html) */}
				<Rect
					width={canvas_w}
					height={canvas_h}
					opacity={0}
					fill='white'
					preventDefault={false}
				/>

				{(show_grid) && (
					<EditableArea
						areaMove={moveGeneral}
						area={relative_group_area}
						areaChanged={(area) => props.areaChanged('group', area)}
						guides_color={guides_color}
						is_editing={true}
						size_w={canvas_w}
						size_h={canvas_h}
						allow_resize={false}
					/>
				)}
			</Layer>
		</Stage>
	)
})

export default PunchCardCanvas;